import React, { useCallback, useEffect, useRef, useMemo, type ComponentProps } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import PageLiveDocIcon from '@atlaskit/icon-lab/core/page-live-doc';

import { Ati } from '@atlassian/ari';

import { useHardStorageEnforcement } from '@confluence/storage-enforcement/entry-points/HardEnforcement/useHardStorageEnforcement.ts';
import { CreateButtonSpotlightPulseWrapper } from '@confluence/onboarding-live-doc-changeboarding/entry-points/CreateButtonSpotlightPulseWrapper.ts';
import { CreateLiveDocPopoverItemSpotlight } from '@confluence/onboarding-live-doc-changeboarding/entry-points/CreateLiveDocPopoverItemSpotlight.ts';
import { useLiveDocChangeboarding } from '@confluence/onboarding-live-doc-changeboarding/entry-points/useLiveDocChangeboarding.ts';
import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { fg } from '@confluence/feature-gating';
import {
	checkShouldShowLozenge,
	setLozengeShownTrackerMax,
} from '@confluence/live-pages-utils/entry-points/liveDocsLozengeUtils.ts';

import type { UseMenuItemProps, UseMenuItemReturnProps } from './index.tsx';

import { buildCreationLink } from '../../CreateContentRouteComponent/index.ts';
import { useHybridLinkClick } from '../../CreateContentPopover/CreateContentPopoverItems/hybridLinkHelper.ts';
import { ConfluencePageSubType } from '../../queries/__types__/CreateContentMutation.ts';
import { ItemTitleWithLozenge } from '../ItemTitle.tsx';

const messages = defineMessages({
	title: {
		id: 'create-blank-fabric-page.universal-create.liveDoc.title',
		defaultMessage: 'Live Doc',
		description: 'title indicating that this option will create a live doc',
	},
	newLozenge: {
		id: 'create-blank-fabric-page.universal-create.liveDoc.new.lozenge',
		defaultMessage: 'New',
		description: 'Text for the new lozenge. Informs users that the associated feature is new.',
	},
});

const CREATE_LIVE_DOC_POPOVER_ITEM = 'create-live-doc-popover-item';

export function useLiveDocItem({
	parentId,
	source,
	spaceKey,
	onClick,
	hideLozenge,
}: UseMenuItemProps): UseMenuItemReturnProps {
	const { formatMessage } = useIntl();
	const { enforceStorageLimit, shouldBlockCreate } = useHardStorageEnforcement({
		source: CREATE_LIVE_DOC_POPOVER_ITEM,
	});

	const createHref = buildCreationLink({
		contentType: 'page',
		subType: ConfluencePageSubType.LIVE,
		parentPageId: parentId,
		withFallback: true,
		source,
		spaceKey,
	});

	const hybridLinkClick = useHybridLinkClick(createHref);
	const handleClick = (e: React.MouseEvent | React.KeyboardEvent) => {
		onClick('createLiveDocPopoverItem');
		hybridLinkClick(e);
		setLozengeShownTrackerMax(); // Disable live docs lozenge after user creates live doc
	};

	// shouldShowLozenge should be stabilized so it is not checked each re-render
	// we also do not want the lozenge to disappear if it's already shown, even if tracker value changes
	const shouldShowLozenge = useMemo(() => checkShouldShowLozenge(), []);
	// Lozenge is always removed only on Hello.
	const titleItem =
		fg('confluence_remove_live_page_lozenge_hello') || !shouldShowLozenge || hideLozenge ? (
			formatMessage(messages.title)
		) : (
			<ItemTitleWithLozenge lozenge={formatMessage(messages.newLozenge)}>
				{formatMessage(messages.title)}
			</ItemTitleWithLozenge>
		);

	return {
		ati: Ati.parse('ati:cloud:confluence:livedoc'),
		children: titleItem,
		onClick: enforceStorageLimit(handleClick),
		elemBefore: PageLiveDocIcon,
		...(!shouldBlockCreate && { href: createHref }),
		Wrapper: (props) => (
			<LiveDocSpotlightWrapper
				{...props}
				source={source}
				createHref={createHref}
				handleClick={handleClick}
			/>
		),
		testId: `create-menu-${shouldBlockCreate ? 'button' : 'link'}-item`,
	};
}

const LiveDocSpotlightWrapper = React.memo(
	({
		children,
		source,
		createHref,
		handleClick,
	}: {
		children: ComponentProps<typeof CreateButtonSpotlightPulseWrapper>['children'];
		source: ComponentProps<typeof CreateLiveDocPopoverItemSpotlight>['source'];
		createHref: string;
		handleClick: (e: React.MouseEvent | React.KeyboardEvent) => void;
	}) => {
		const hasFiredOnStartEventRef = useRef(false);
		const { push } = useRouteActions();
		const { introSpotlight, error } = useLiveDocChangeboarding();
		const { isVisible: shouldShowIntroSpotlight, complete: completeIntroSpotlight } =
			introSpotlight;
		const { createAnalyticsEvent } = useAnalyticsEvents();
		const { enforceStorageLimit } = useHardStorageEnforcement({
			source: CREATE_LIVE_DOC_POPOVER_ITEM,
		});

		const onSpotlightStart = useCallback(() => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'shown',
					actionSubject: 'spotlight',
					actionSubjectId: 'liveDocIntro',
					source,
				},
			}).fire();
		}, [createAnalyticsEvent, source]);

		useEffect(() => {
			if (shouldShowIntroSpotlight && !hasFiredOnStartEventRef.current) {
				onSpotlightStart();
				hasFiredOnStartEventRef.current = true;
			}
		}, [shouldShowIntroSpotlight, onSpotlightStart]);

		return (
			<CreateButtonSpotlightPulseWrapper
				condition={shouldShowIntroSpotlight}
				wrapper={(element) => (
					<CreateLiveDocPopoverItemSpotlight
						handleDismiss={() => {
							completeIntroSpotlight();
						}}
						handleProceed={enforceStorageLimit((e) => {
							completeIntroSpotlight();
							handleClick(e);
							push(createHref);
						})}
						targetName={CREATE_LIVE_DOC_POPOVER_ITEM}
						source={source}
						fireAnalyticsEventOnMessageDisposition={false} // default is true, manually disabling it
					>
						{element}
						{error && <ErrorDisplay error={error} />}
					</CreateLiveDocPopoverItemSpotlight>
				)}
			>
				{children()}
			</CreateButtonSpotlightPulseWrapper>
		);
	},
);
